import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import {
  serviceCategories,
  getServicesByCategory,
  getAllServices,
} from '../data/servicesData';
import { useBooking } from '../context/BookingContext';
import SEO from '../components/SEO';

const SERVICES_PAGE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Aesthetic & Precision Health Services',
  about: {
    '@type': 'Thing',
    name: 'Medical spa services in Alhambra, CA — aesthetic treatments, precision health, and wellness support.',
  },
  isPartOf: { '@id': 'https://akirasignaturecare.com/#website' },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://akirasignaturecare.com/' },
      { '@type': 'ListItem', position: 2, name: 'Services', item: 'https://akirasignaturecare.com/services' },
    ],
  },
};

const ServicesPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const { openBooking } = useBooking();

  const services =
    activeCategory === 'all'
      ? getAllServices()
      : getServicesByCategory(activeCategory);

  const categories = {
    all: {
      name: 'All Services',
      description:
        'Explore our complete range of aesthetic treatments, precision health programmes, and wellness support.',
    },
    ...serviceCategories,
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="services-page">
      <SEO
        title="All Services"
        description="Browse 18 physician-led treatments: XERF, Thulium & CO2 laser, IPL, microneedling, Botox, fillers, PRP, GLP-1, hormones, and longevity medicine."
        path="/services"
        schema={SERVICES_PAGE_SCHEMA}
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-rose-900 text-white py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-rose-300 text-sm mb-4">
              <Sparkles className="w-4 h-4" />
              <span>COMPREHENSIVE CARE</span>
            </div>
            <h1
              className="font-serif text-5xl lg:text-6xl mb-6"
              data-testid="services-page-title"
            >
              Our Services
            </h1>
            <p className="text-xl text-stone-300 leading-relaxed">
              From advanced aesthetic treatments to precision health
              optimisation, we offer comprehensive care tailored to your unique
              goals.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filters */}
      <section className="bg-white border-b">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-wrap gap-3">
            {Object.entries(categories).map(([key, category]) => (
              <button
                key={key}
                data-testid={`services-filter-${key}`}
                onClick={() => setActiveCategory(key)}
                className={`px-6 py-3 rounded-full font-medium transition-all ${
                  activeCategory === key
                    ? 'bg-rose-400 text-white shadow-lg'
                    : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="mb-8">
            <h2 className="font-serif text-3xl text-stone-900 mb-3">
              {categories[activeCategory].name}
            </h2>
            <p className="text-stone-600 text-lg">
              {categories[activeCategory].description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Link
                key={service.id}
                to={`/services/${service.slug}`}
                data-testid={`service-card-${service.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300"
              >
                {/* Service Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {service.badge && (
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-stone-800">
                      {service.badge}
                    </div>
                  )}
                </div>

                {/* Service Info */}
                <div className="p-6">
                  <h3 className="font-serif text-2xl text-stone-900 mb-2 group-hover:text-rose-500 transition-colors">
                    {service.name}
                  </h3>
                  <p className="text-stone-600 text-sm mb-3">
                    {service.tagline}
                  </p>
                  <p className="text-stone-700 mb-5 line-clamp-3 text-sm leading-relaxed">
                    {service.shortDescription}
                  </p>

                  <div className="flex items-center justify-end pt-4 border-t border-stone-100 text-rose-500 group-hover:text-rose-600 transition-colors">
                    <span className="text-sm font-medium mr-2">Learn more</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-stone-900 to-rose-900 text-white py-20">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl mb-6">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-xl text-stone-300 mb-8 max-w-2xl mx-auto">
            Schedule a consultation to discuss your goals and
            create a personalised treatment plan.
          </p>
          <button
            onClick={() => openBooking()}
            data-testid="services-page-cta-book"
            className="inline-flex items-center gap-2 bg-rose-400 hover:bg-rose-500 text-white px-10 py-4 rounded-full font-semibold text-lg transition-colors"
          >
            Book a Consultation
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
